import React, { useState, useContext, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import {
  TmoButtons,
  withModal
} from '@tmobile/tmo-bps-syncup-web-component-lib';

// Service to fetch feature flags from backend
import featureFlagService from '../../services/featureFlagsService';

// Generic data-fetching wrapper component
import Fetcher from '../Fetcher';

// Context providing SyncUp application-level details
import SyncUpProductContext from '../../context/SyncUpProductContext';

// Utility to generate unique keys
import { getRandomString } from '../../utils/stringUtils';

// Utility to check for non-empty values
import { hasValue } from '../../utils/helper_functions';

// Error UI component
import Error from '../../components/Error/Error';

// Page header UI component
import PageHeader from '../../components/uiHelpers/pageHeader/PageHeader';

// CSS module for styling
import style from './FeatureFlags.module.css';

function FeatureFlags({ match: { url }, history, openModal }) {
  // Access global SyncUp product context
  const syncUpProductContext = useContext(SyncUpProductContext);

  // Holds the list of feature flags fetched from API
  const [featureFlags, setFeatureFlags] = useState([]);

  // Used to force re-mount Fetcher component and trigger reload
  const [refreshKey, setRefreshKey] = useState(
    getRandomString() + syncUpProductContext.syncUpAppName
  );

  /**
   * Forces Fetcher to re-fetch data
   * by changing its `key` value
   */
  const forceRefresh = useCallback(() => {
    setRefreshKey(getRandomString() + syncUpProductContext.syncUpAppName);
  }, [syncUpProductContext.syncUpAppName]);

  /**
   * API call used by Fetcher to load feature flags
   */
  const actionCallback = useCallback(async () => {
    try {
      const flags = await featureFlagService.getFeatureFlags({
        syncUpAppName: syncUpProductContext.syncUpAppName
      });
      return flags;
    } catch (error) {
      console.error('Error loading feature flags:', error);
      throw error;
    }
  }, [syncUpProductContext.syncUpAppName]);

  /**
   * Called by Fetcher once data is successfully loaded
   */
  const handleLoadData = useCallback(data => {
    if (data) {
      setFeatureFlags(data);
    }
  }, []);

  return (
    <>
      {/* Page header with refresh action */}
      <PageHeader
        primary
        title={'Feature Flags'}
        subtitle={
          'Manage feature flags for the ' +
          syncUpProductContext.syncUpAppName +
          ' application.'
        }
      >
        <TmoButtons onClick={forceRefresh} type="magenta_primary">
          Refresh
        </TmoButtons>
      </PageHeader>

      <div>
        {/* Fetcher handles async loading, errors, and re-fetching */}
        <Fetcher
          key={refreshKey}
          action={actionCallback}
          onLoad={handleLoadData}
          render={() => {
            return hasValue(featureFlags) ? (
              <>
                {/* Feature flag list */}
                <div className={style.items}>
                  {featureFlags.map(flag => (
                    <div key={flag.featureId} className={style.item}>
                      {/* Feature flag identifier */}
                      <div className={style.featureId}>{flag.featureId}</div>

                      {/* Feature flag status */}
                      <div className={style.status}>
                        {flag.isEnabled ? 'Enabled' : 'Disabled'}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              // Empty state when no feature flags exist
              <Error
                heading="No Feature Flags are available"
                message="Please add a new feature flag"
              />
            );
          }}
        />
      </div>
    </>
  );
}

// Export component with routing and modal capabilities
export default withModal(withRouter(FeatureFlags));
